import styled from "styled-components";
import "swiper/css";

interface WeatherSidebarProps {
  primaryColor: string;
  secondaryColor: string;
}

const SidebarContainer = styled.div<{ primaryColor: string }>`
  background: ${(props) => props.primaryColor};
  width: 182px;
  height: 322px;
  padding-top: 26px;
  padding-left: 17px;
  display: flex;
  flex-direction: column;
`;

const LocationHeading = styled.h3<{ secondaryColor: string }>`
  font-family: var(--sora-font);
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.secondaryColor};
  margin-bottom: 6px;
`;

const DateContainer = styled.div`
  display: flex;
  column-gap: 4px;
  margin-bottom: 30px;
`;

const TodayLabel = styled.div<{
  primaryColor: string;
  secondaryColor: string;
}>`
  color: ${(props) => props.primaryColor};
  width: 30px;
  height: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => props.secondaryColor};
  border-radius: 1.2px;
  font-family: var(--lato-font);
  font-size: 6px;
`;

const DateText = styled.div<{ secondaryColor: string }>`
  font-family: var(--lato-font);
  font-size: 6px;
  color: ${(props) => props.secondaryColor};
`;

const WeatherInfo = styled.div<{ secondaryColor: string }>`
  color: ${(props) => props.secondaryColor};
  margin-bottom: 24px;
`;

const Temperature = styled.p`
  font-family: var(--lato-font);
  font-size: 33px;
  font-weight: 700;
  margin-bottom: 3px;
`;

const TempUnit = styled.span`
  vertical-align: super;
  font-family: var(--lato-font);
  font-size: 18px;
  font-weight: 700;
`;

const FeelsLike = styled.p`
  font-family: var(--lato-font);
  font-size: 8px;
  margin-bottom: 6px;
`;

const WeatherDescription = styled.p`
  font-size: 6px;
`;

const ForecastContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  column-gap: 6.5px;
  row-gap: 5.6px;
`;

const ForecastBlock = styled.div`
  width: 71px;
  height: 21px;
  border-radius: 1.2px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2.6px 2.8px;
`;

const ForecastText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ForecastDay = styled.p<{ secondaryColor: string }>`
  font-family: var(--lato-font);
  font-size: 5px;
  color: ${(props) => props.secondaryColor};
`;

const ForecastTemp = styled.p<{ secondaryColor: string }>`
  font-family: var(--lato-font);
  font-size: 5.5px;
  color: ${(props) => props.secondaryColor};
`;

export const WeatherSidebar = (props: WeatherSidebarProps): JSX.Element => {
  const { primaryColor, secondaryColor } = props;
  return (
    <SidebarContainer primaryColor={primaryColor}>
      <LocationHeading secondaryColor={secondaryColor}>
        United Kingdom
      </LocationHeading>
      <DateContainer>
        <TodayLabel primaryColor={primaryColor} secondaryColor={secondaryColor}>
          Today
        </TodayLabel>
        <DateText secondaryColor={secondaryColor}>
          <p>Monday</p>
          <p>02:26 PM</p>
        </DateText>
      </DateContainer>
      <div
        style={{
          marginBottom: "10px",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="45"
          height="30"
          style={{
            width: "45px",
            height: "30px",
          }}
          viewBox="0 0 25 18"
          fill="none"
        >
          <path
            d="M13.0876 1.99816C15.4875 1.99816 17.491 3.68542 17.981 5.93871C18.053 5.936 18.1249 5.93329 18.1982 5.93329C20.9646 5.93329 23.2057 8.17572 23.2057 10.9408C23.2057 13.7058 20.9633 15.9482 18.1982 15.9482H6.35212C3.60473 15.9252 1.38536 13.6922 1.38536 10.9408C1.38536 8.1893 3.6278 5.93329 6.39284 5.93329C7.00639 5.93329 7.59415 6.04459 8.13711 6.24549C8.50361 3.84016 10.5804 1.99816 13.0876 1.99816ZM13.0876 0.912231C11.6216 0.912231 10.2044 1.44026 9.09816 2.39995C8.30815 3.08408 7.71224 3.95554 7.35932 4.92473C7.04168 4.87451 6.71862 4.84872 6.39284 4.84872C3.03326 4.84736 0.299438 7.58118 0.299438 10.9408C0.299438 12.5602 0.927918 14.0845 2.06814 15.2329C2.95046 16.122 4.05946 16.7057 5.26619 16.9296V17.0328H6.35212H18.1982C21.5578 17.0328 24.2916 14.299 24.2916 10.9394C24.2916 7.78072 21.8768 5.17585 18.7968 4.87451C18.4195 3.86731 17.7774 2.96599 16.9372 2.2805C15.854 1.39818 14.4871 0.912231 13.0876 0.912231Z"
            fill={secondaryColor}
          />
        </svg>
      </div>
      <WeatherInfo secondaryColor={secondaryColor}>
        <Temperature>
          16
          <TempUnit>°C</TempUnit>
        </Temperature>
        <FeelsLike>Feels like 16°</FeelsLike>
        <WeatherDescription>Partly sunny. Cool.</WeatherDescription>
      </WeatherInfo>
      <ForecastContainer>
        {Array(4)
          .fill(0)
          .map((element, i) => (
            <ForecastBlock key={`weather-block-${i}`}>
              <ForecastText>
                <ForecastDay secondaryColor={secondaryColor}>
                  Tuesday
                </ForecastDay>
                <ForecastTemp secondaryColor={secondaryColor}>
                  14° / 8°
                </ForecastTemp>
              </ForecastText>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8.8"
                height="6"
                style={{
                  width: "8.8px",
                  height: "6px",
                }}
                viewBox="0 0 25 18"
                fill="none"
              >
                <path
                  d="M13.0876 1.99816C15.4875 1.99816 17.491 3.68542 17.981 5.93871C18.053 5.936 18.1249 5.93329 18.1982 5.93329C20.9646 5.93329 23.2057 8.17572 23.2057 10.9408C23.2057 13.7058 20.9633 15.9482 18.1982 15.9482H6.35212C3.60473 15.9252 1.38536 13.6922 1.38536 10.9408C1.38536 8.1893 3.6278 5.93329 6.39284 5.93329C7.00639 5.93329 7.59415 6.04459 8.13711 6.24549C8.50361 3.84016 10.5804 1.99816 13.0876 1.99816ZM13.0876 0.912231C11.6216 0.912231 10.2044 1.44026 9.09816 2.39995C8.30815 3.08408 7.71224 3.95554 7.35932 4.92473C7.04168 4.87451 6.71862 4.84872 6.39284 4.84872C3.03326 4.84736 0.299438 7.58118 0.299438 10.9408C0.299438 12.5602 0.927918 14.0845 2.06814 15.2329C2.95046 16.122 4.05946 16.7057 5.26619 16.9296V17.0328H6.35212H18.1982C21.5578 17.0328 24.2916 14.299 24.2916 10.9394C24.2916 7.78072 21.8768 5.17585 18.7968 4.87451C18.4195 3.86731 17.7774 2.96599 16.9372 2.2805C15.854 1.39818 14.4871 0.912231 13.0876 0.912231Z"
                  fill={secondaryColor}
                />
              </svg>
            </ForecastBlock>
          ))}
      </ForecastContainer>
    </SidebarContainer>
  );
};
